import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User } from "lucide-react";

// Team data — real qualifications/roles supplied by client. No fake photographs.
const FOUNDERS = [
  {
    id: "yuvraj",
    photo: "/media/team/yuvraj.jpg",
    pos: "50% 0%",
    name: "Dr Yuvraj (PT)",
    role: "FOUNDER · TEAM PRINCIPAL",
    quals: "Master of Physiotherapy · CrossFit Level 1 · PGDM",
    extra: "Program Director, D Y Patil Sports & High Performance Centre",
    bio: "Team Principal, Positive Physiotherapy & PLST — leading the clinical and training philosophy behind how we help people move, recover and become stronger.",
  },
  {
    id: "pradnya",
    photo: "/media/team/pradnya.jpg",
    pos: "50% 5%",
    name: "Dr Pradnya (PT)",
    role: "FOUNDER · LEAD, THANE CENTRE",
    quals: "Master of Physiotherapy · Certified Kinetic Control Movement Therapist",
    extra: "",
    bio: "Leads our Thane centre and plays a key role in developing learning systems and training modules that shape how our physiotherapists think, learn and deliver care.",
  },
];

const TEAM = [
  {
    id: "priya",
    photo: "/media/team/priya.jpg",
    pos: "50% 0%",
    name: "Dr Priya (PT)",
    role: "SPORTS PHYSIOTHERAPIST · ATHLETE DEVELOPMENT",
    quals: "Master of Physiotherapy · ACE Certified Performance Enhancement Specialist",
    bio: "Works at the intersection of sports physiotherapy and performance, helping athletes build the strength and physical capacity needed for long-term development.",
  },
  {
    id: "dhara",
    photo: "/media/team/dhara.jpg",
    pos: "50% 0%",
    name: "Dr Dhara (PT)",
    role: "MUSCULOSKELETAL PHYSIOTHERAPIST",
    quals: "Master of Physiotherapy",
    bio: "Focuses on screening and assessment, helping us understand where each person is starting from and guiding the right pathway towards movement and strength.",
  },
  {
    id: "ulka",
    photo: "/media/team/ulka.jpg",
    pos: "50% 0%",
    name: "Dr Ulka (PT)",
    role: "PHYSIOTHERAPIST · NASM CERTIFIED STRENGTH COACH",
    quals: "Physiotherapist · NASM Certified Strength Coach",
    bio: "Works with the general population, with a particular interest in helping women become stronger and more confident with strength training.",
  },
  {
    id: "aafreen",
    photo: "/media/team/aafreen.jpg",
    pos: "50% 0%",
    name: "Dr Aafreen (PT)",
    role: "PHYSIOTHERAPIST · REHABILITATION",
    quals: "Master of Physiotherapy",
    bio: "Works with rehabilitation clients, with a particular interest in musculoskeletal rehabilitation and helping people progress back towards exercise.",
  },
  {
    id: "priyal",
    photo: "/media/team/priyal.jpg",
    pos: "50% 0%",
    name: "Dr Priyal (PT)",
    role: "PHYSIOTHERAPIST · VILE PARLE CENTRE",
    quals: "Physiotherapist · Vile Parle Centre",
    bio: "Plays a key role in PLST operations and leads training sessions, helping clients progress confidently through their individual programs.",
  },
  {
    id: "bilwa",
    photo: "/media/team/bilwa.jpg",
    pos: "50% 0%",
    name: "Dr Bilwa (PT)",
    role: "PHYSIOTHERAPIST · MOVEMENT & YOGA",
    quals: "Master of Physiotherapy",
    bio: "Brings additional expertise in yoga and asana-based movement, integrating mobility, movement control and strength into her approach.",
  },
  {
    id: "srushti",
    photo: "/media/team/srushti.jpg",
    pos: "50% 0%",
    name: "Dr Srushti (PT)",
    role: "PHYSIOTHERAPIST · THANE CENTRE",
    quals: "Physiotherapist · Thane Centre",
    bio: "Leads PLST sessions at our Thane centre, guiding clients through their individual strength programs and progressions.",
  },
  {
    id: "laxmi",
    photo: "/media/team/laxmi.jpg",
    pos: "50% 0%",
    name: "Dr Laxmi (PT)",
    role: "PHYSIOTHERAPIST · VILE PARLE",
    quals: "Master of Physiotherapy · Pilates Specialist",
    bio: "Brings her expertise in Pilates and movement-based exercise to PLST sessions at our Vile Parle centre.",
  },
  {
    id: "yash",
    photo: "/media/team/yash.jpg",
    pos: "50% 0%",
    name: "Dr Yash (PT)",
    role: "PHYSIOTHERAPIST · ATHLETE DEVELOPMENT",
    quals: "Physiotherapist",
    bio: "Has a keen interest in Athlete Development, strength training and helping athletes build the physical foundations for sport.",
  },
];

// Consistent editorial treatment applied to every real portrait:
// - Portraits are pre-composited onto a dark neutral (#222) via PIL and
//   colour-reduced ~85% so lighting/colour differences don't fight each other.
// - Card frame sits on the same dark tone; a subtle bottom gradient keeps
//   type crisp against the image on the card.
function Portrait({ src, alt, tall, position = "50% 12%" }) {
  return (
    <div className={`relative w-full ${tall ? "aspect-[4/5]" : "aspect-[3/4]"} overflow-hidden bg-[#222222] border border-[#1E1E1E]/10`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: position }}
        draggable={false}
      />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.35))",
      }} />
    </div>
  );
}

function PhotoPlaceholder({ tall }) {
  return (
    <div className={`w-full ${tall ? "aspect-[4/5]" : "aspect-[3/4]"} bg-[#F5F4EF] border border-[#1E1E1E]/10 flex items-center justify-center overflow-hidden`}>
      <User size={40} strokeWidth={1} className="text-[#1E1E1E]/25" />
    </div>
  );
}

function MemberCard({ m, tall = false, onOpen }) {
  return (
    <div className="group" data-testid={`team-${m.id}`}>
      <button
        onClick={() => onOpen(m)}
        className="block w-full text-left"
        aria-label={`Meet ${m.name}`}
        data-testid={`team-open-${m.id}`}
      >
        {m.photo ? <Portrait src={m.photo} alt={m.name} tall={tall} position={m.pos} /> : <PhotoPlaceholder tall={tall} />}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#A67C00] font-semibold">{m.role}</div>
          <h3 className="mt-2 font-display font-semibold text-lg lg:text-xl tracking-tight text-[#1E1E1E]">{m.name}</h3>
          <div className="mt-1 text-xs text-[#1E1E1E]/60">{m.quals}</div>
          {m.extra && <div className="mt-1 text-xs text-[#1E1E1E]/50">{m.extra}</div>}
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-[#1E1E1E]/70 group-hover:text-[#A67C00] transition-colors">
            Meet the team <span className="transition-transform group-hover:translate-x-1">→</span>
          </div>
        </div>
      </button>
    </div>
  );
}

export default function Team() {
  const [active, setActive] = useState(null);
  return (
    <section id="team" className="py-24 lg:py-40 bg-white border-y border-[#1E1E1E]/10" data-testid="team-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }} className="md:col-span-7"
          >
            <div className="text-xs uppercase tracking-[0.28em] text-[#8A8A8A]">
              <span className="w-8 h-px bg-[#A67C00] inline-block align-middle mr-3" />
              The Team
            </div>
            <h2 className="mt-6 italic font-medium text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Physiotherapists <br />
              <span className="text-[#A67C00]">who understand strength.</span>
            </h2>
          </motion.div>
          <p className="md:col-span-5 text-[#1E1E1E]/70 leading-relaxed">
            We are physiotherapists who believe movement and strength are an essential part of good healthcare. We understand pain and injury — but we also understand exercise, load and progression.
          </p>
        </div>

        {/* Founders — featured */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 mb-12 lg:mb-16">
          {FOUNDERS.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <MemberCard m={m} tall onOpen={setActive} />
            </motion.div>
          ))}
        </div>

        {/* Rest of team — compact grid, 3 across on desktop for the 9-person roster */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
          {TEAM.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
            >
              <MemberCard m={m} onOpen={setActive} />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bio modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="team-modal"
          >
            <div className="absolute inset-0 bg-[#1E1E1E]/60" onClick={() => setActive(null)} />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
              className="relative w-full max-w-lg bg-[#F5F4EF] border border-[#1E1E1E]/10 p-8 lg:p-10"
            >
              <button
                onClick={() => setActive(null)}
                data-testid="team-modal-close"
                className="absolute top-4 right-4 p-2 text-[#1E1E1E]/60 hover:text-[#A67C00]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#A67C00] font-semibold">{active.role}</div>
              <h3 className="mt-3 font-display font-semibold text-2xl lg:text-3xl tracking-tight text-[#1E1E1E]">{active.name}</h3>
              <div className="mt-2 text-sm text-[#1E1E1E]/70">{active.quals}</div>
              {active.extra && <div className="mt-1 text-sm text-[#1E1E1E]/60 italic">{active.extra}</div>}
              <p className="mt-6 text-base text-[#1E1E1E]/80 leading-relaxed">{active.bio}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
